import { useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type ProductClientCollections } from "@fitness-app/data-models/entities/ProductClient";

import { productApi } from "~/store/initializeStore";

export interface InstallmentGatewaySummary {
  paid: number;
  failed: number;
  // card raty Stripe is still retrying — not in the collection rate
  retrying: number;
  refunded: number;
  pending: number;
  overdue: number;
  paidAmount: number; // cents
  overdueAmount: number; // cents
  collectionRate: number | null; // %
}

export interface InstallmentCycleSummary {
  paymentCycle: number;
  card: InstallmentGatewaySummary;
  manual: InstallmentGatewaySummary;
}

export interface InstallmentMonthlySummary {
  month: string; // "2026-06"
  card: InstallmentGatewaySummary;
  manual: InstallmentGatewaySummary;
}

export interface InstallmentRetentionPoint {
  paymentCycle: number;
  cardEligible: number;
  cardReached: number;
  cardRate: number | null;
  manualEligible: number;
  manualReached: number;
  manualRate: number | null;
}

export interface InstallmentCohortSummary {
  cohortMonth: string;
  kind: "card" | "manual";
  totalClients: number;
  reachedCycle2: number;
  reachedCycle3: number;
  reachedCycle6: number;
  avgPaidCycles: number;
}

export type OverdueInstallmentSource = "manual_invoice" | "card_exhausted" | "card_retrying";

export interface OverdueInstallmentItem {
  orderId: string;
  clientEmail: string;
  clientName: string | null;
  clientStatus: "active" | "archived";
  /** `product_client.id` — the record the collections case is keyed by. */
  clientId: string;
  productId: string;
  productName: string | null;
  paymentCycle: number | null;
  amount: number | null; // cents
  currency: string | null;
  dueDate: string;
  daysOverdue: number;
  invoiceId: string | null;
  source: OverdueInstallmentSource;
  attemptCount: number | null;
  nextPaymentAttemptAt: string | null;
  collections: ProductClientCollections | null;
  plan: { installments: number | null; cycleNumber: number | null; installmentEndsAt: string | null };
}

export interface OverdueInstallmentBucket {
  invoices: number;
  clients: number;
  amount: number; // cents
}

export interface InstallmentCollectionStatsResponse {
  generatedAt: string;
  fromDate: string;
  manualDueDays: number;
  summary: {
    // actionable debt of active clients: manual past due + card exhausted
    overdueInvoices: number;
    overdueAmount: number; // cents
    overdueClients: number;
    retrying: OverdueInstallmentBucket;
    archived: OverdueInstallmentBucket;
    cardCollectionRate: number | null;
    manualCollectionRate: number | null;
  };
  byCycle: InstallmentCycleSummary[];
  monthly: InstallmentMonthlySummary[];
  retention: InstallmentRetentionPoint[];
  cohorts: InstallmentCohortSummary[];
  overdue: OverdueInstallmentItem[];
}

const EMPTY_BUCKET: OverdueInstallmentBucket = { invoices: 0, clients: 0, amount: 0 };

/**
 * The admin and products-service deploy separately, so for a while the panel
 * may read a response from before TRE-2167/2168. Fill what an older service
 * does not send instead of letting the tab crash on `summary.retrying`.
 */
const normalizeStats = (data: InstallmentCollectionStatsResponse): InstallmentCollectionStatsResponse => ({
  ...data,
  summary: {
    ...data.summary,
    overdueClients: data.summary.overdueClients ?? 0,
    retrying: data.summary.retrying ?? EMPTY_BUCKET,
    archived: data.summary.archived ?? EMPTY_BUCKET,
  },
  overdue: (data.overdue ?? []).map((item) => ({
    ...item,
    orderId: item.orderId ?? item.invoiceId ?? item.clientEmail,
    clientId: item.clientId ?? item.clientEmail,
    clientStatus: item.clientStatus ?? "active",
    attemptCount: item.attemptCount ?? null,
    nextPaymentAttemptAt: item.nextPaymentAttemptAt ?? null,
    collections: item.collections ?? null,
    plan: item.plan ?? { installments: null, cycleNumber: null, installmentEndsAt: null },
  })),
});

export const useInstallmentCollectionStats = (params: { productId: string; months: number }) => {
  return useQuery<InstallmentCollectionStatsResponse, AxiosError>({
    queryKey: ["installmentCollectionStats", params.productId, params.months],
    enabled: Boolean(params.productId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await productApi.get<InstallmentCollectionStatsResponse>("/installments/collection-stats", {
        params: {
          months: params.months,
          productId: params.productId,
        },
      });
      return normalizeStats(response.data);
    },
  });
};
